/**
 * FieldworkPanels — Phase II helpers of the CMS program-audit process.
 *
 * ConferencesPanel: the entrance / status / exit conference record, rendered
 * as a small timeline. The entrance conference opens fieldwork, status calls
 * keep both sides current, the exit conference closes it out.
 *
 * AnalysisRequestsPanel: requests to the auditee — root-cause and impact
 * analyses on the CMS clock (root cause due in 2 business days, impact in
 * 10), plus plain document requests and questions with an explicit due date.
 * Each request tracks its due date (overdue is highlighted), the status of
 * the auditee's response, and the response content itself; every request can
 * be emailed straight from the correspondence center, or marked visible to
 * the auditee so it appears on their portal page, where they answer and
 * upload directly.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { CONFERENCE_KIND_LABELS, ANALYSIS_KIND_LABELS, ANALYSIS_STATUS_LABELS } from './labels.js';

const API = process.env.REACT_APP_API_URL;

export function ConferencesPanel({ auditId }) {
  const [conferences, setConferences] = useState([]);
  const [kind, setKind] = useState('entrance');
  const [heldAt, setHeldAt] = useState('');
  const [attendees, setAttendees] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/conferences`, { credentials: 'include' }).then(r => r.json());
      if (r.success) setConferences(r.conferences || []);
    } catch {}
  }, [auditId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/api/audit/audits/${auditId}/conferences`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, held_at: heldAt || null, attendees: attendees.trim() || null, notes: notes.trim() || null }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || 'Could not record the conference.');
      setHeldAt(''); setAttendees(''); setNotes('');
      await load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div>
        <div className="text-sm font-semibold text-slate-800">Conferences{conferences.length ? <span className="text-slate-400 font-normal"> · {conferences.length}</span> : null}</div>
        <div className="text-[11px] text-slate-400">The entrance conference opens fieldwork, status calls keep both sides current, and the exit conference closes it out. Record who was there.</div>
      </div>

      {conferences.length > 0 && (
        <div className="space-y-2">
          {conferences.map(c => (
            <div key={c.id} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[12px] text-slate-700">
                  <span className="font-semibold">{CONFERENCE_KIND_LABELS[c.kind] || c.kind}</span>
                  {c.held_at && <span className="text-slate-400"> · {new Date(c.held_at).toLocaleString()}</span>}
                </div>
                {c.attendees && <div className="text-[11px] text-slate-500">With: {c.attendees}</div>}
                {c.notes && <div className="text-[11px] text-slate-400">{c.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-[auto,auto,1fr,auto] gap-2 items-start bg-slate-50 border border-slate-100 rounded-lg p-2">
        <select value={kind} onChange={e => setKind(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700">
          {Object.entries(CONFERENCE_KIND_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input type="datetime-local" value={heldAt} onChange={e => setHeldAt(e.target.value)} aria-label="When it was held" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
        <input value={attendees} onChange={e => setAttendees(e.target.value)} placeholder="Attendees" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 min-w-0" />
        <button type="button" onClick={add} disabled={busy} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">Add</button>
        <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" className="sm:col-span-4 text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}
    </div>
  );
}

function AnalysisRow({ r, auditId, findingTitle, documents = [], onChanged, onEmail }) {
  const [content, setContent] = useState(r.content || '');
  const [busy, setBusy] = useState(false);

  const patch = async (body) => {
    setBusy(true);
    try {
      const d = await fetch(`${API}/api/audit/audits/${auditId}/analysis-requests/${r.id}`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      }).then(r => r.json());
      if (d.success) onChanged();
    } catch {}
    setBusy(false);
  };

  const overdue = r.due_date && !['received', 'accepted'].includes(r.status)
    && String(r.due_date).slice(0, 10) < new Date().toISOString().slice(0, 10);
  const visible = r.visible_to_auditee === true;
  // The auditee's portal uploads are labeled "[Auditee] {title-or-kind}" by
  // the server, which links each document back to its request here.
  const linkedUploads = documents.filter(d => d.label === `[Auditee] ${String(r.title || r.kind || 'response').slice(0, 280)}`);

  return (
    <div className={`border rounded-lg px-3 py-2 space-y-1.5 ${overdue ? 'border-red-200 bg-red-50/50' : 'border-slate-100'}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button" disabled={busy}
          aria-label={visible ? 'Visible to auditee' : 'Not visible to auditee'}
          title={visible ? 'The auditee can see and answer this on their portal page. Click to make it internal-only.' : 'Internal-only. Click to show it to the auditee on their portal page.'}
          onClick={() => patch({ visible_to_auditee: !visible })}
          className={`flex-shrink-0 disabled:opacity-50 ${visible ? 'text-orange-600' : 'text-slate-300 hover:text-slate-500'}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
        <span className="text-xs font-semibold text-slate-700">{ANALYSIS_KIND_LABELS[r.kind] || r.kind}</span>
        {r.title && <span className="text-xs text-slate-600">· {r.title}</span>}
        {visible && <span className="text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-full px-1.5 py-0.5">Auditee can see this</span>}
        {findingTitle && <span className="text-[10px] text-orange-600">→ {findingTitle}</span>}
        {r.due_date && (
          <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border ${overdue ? 'bg-red-100 text-red-700 border-red-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
            {overdue ? 'Overdue — was due ' : 'Due '}{String(r.due_date).slice(0, 10)}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {onEmail && <button type="button" onClick={() => onEmail(r)} className="text-[11px] font-semibold text-orange-600">Email this request</button>}
          <select value={r.status} disabled={busy} onChange={e => patch({ status: e.target.value })} className="text-xs border border-slate-300 rounded-lg px-2 py-1 text-slate-700">
            {Object.entries(ANALYSIS_STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </span>
      </div>
      <div className="text-[10px] text-slate-400">
        Requested {r.requested_at ? new Date(r.requested_at).toLocaleDateString() : ''}
        {r.received_at ? ` · received ${new Date(r.received_at).toLocaleDateString()}` : ''}
      </div>
      {r.responded_at && (
        <div className="bg-emerald-50/60 border border-emerald-100 rounded-lg px-3 py-2 space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
            Response received {new Date(r.responded_at).toLocaleDateString()}
          </div>
          {r.response_text && <p className="text-[12px] text-slate-700 whitespace-pre-wrap">{r.response_text}</p>}
          {linkedUploads.map(d => (
            <div key={d.id} className="text-[11px] text-slate-500">Attached: <span className="text-slate-700 font-medium">{d.file_name}</span></div>
          ))}
        </div>
      )}
      {r.status !== 'requested' && (
        <div className="space-y-1">
          <textarea value={content} onChange={e => setContent(e.target.value)} rows={3} placeholder="Paste the auditee's analysis here…" className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5" />
          {content !== (r.content || '') && (
            <button type="button" onClick={() => patch({ content })} disabled={busy} className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-slate-800 text-white disabled:opacity-50">Save the analysis</button>
          )}
        </div>
      )}
    </div>
  );
}

export function AnalysisRequestsPanel({ auditId, findings = [], documents = [], onEmail }) {
  const [requests, setRequests] = useState([]);
  const [findingId, setFindingId] = useState('');
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/analysis-requests`, { credentials: 'include' }).then(r => r.json());
      if (r.success) setRequests(r.requests || []);
    } catch {}
  }, [auditId]);
  useEffect(() => { load(); }, [load]);

  const request = async (kind) => {
    setErr('');
    // Document requests and questions land on the auditee's portal page as
    // their title — never send one without words the auditee will understand.
    if (['documentation', 'question'].includes(kind) && !title.trim()) {
      setErr('Give it a title the auditee will understand.');
      return;
    }
    setBusy(kind);
    try {
      const body = { kind, visible_to_auditee: visible };
      if (findingId) body.finding_id = findingId;
      if (title.trim()) body.title = title.trim();
      if (dueDate) body.due_date = dueDate;
      const res = await fetch(`${API}/api/audit/audits/${auditId}/analysis-requests`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || 'Could not create the request.');
      setFindingId(''); setTitle(''); setDueDate(''); setVisible(false);
      await load();
    } catch (e) { setErr(e.message); }
    setBusy('');
  };

  const titleOf = (fid) => findings.find(f => f.id === fid)?.title || null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div>
        <div className="text-sm font-semibold text-slate-800">Requests to the auditee{requests.length ? <span className="text-slate-400 font-normal"> · {requests.length}</span> : null}</div>
        <div className="text-[11px] text-slate-400">Root cause analyses are due within 2 business days and impact analyses within 10, set automatically on the CMS clock. Document requests and questions take the due date you pick. Mark a request visible and the auditee answers it on their portal page.</div>
      </div>

      <div className="space-y-2 bg-slate-50 border border-slate-100 rounded-lg p-2">
        <div className="grid sm:grid-cols-[minmax(0,1fr),auto] gap-2">
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title the auditee will see (e.g., Provide the Q1 claims payment policy)" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 min-w-0" />
          <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} aria-label="Due date" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {findings.length > 0 && (
            <select value={findingId} onChange={e => setFindingId(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700">
              <option value="">Link to a finding (optional)…</option>
              {findings.map(f => <option key={f.id} value={f.id}>{f.title}</option>)}
            </select>
          )}
          <label className="flex items-center gap-1.5 text-xs text-slate-600 select-none cursor-pointer">
            <input type="checkbox" checked={visible} onChange={e => setVisible(e.target.checked)} /> Visible to auditee
          </label>
          <button type="button" onClick={() => request('root_cause')} disabled={!!busy} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 disabled:opacity-50">
            {busy === 'root_cause' ? 'Requesting…' : 'Request root cause analysis'}
          </button>
          <button type="button" onClick={() => request('impact')} disabled={!!busy} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 disabled:opacity-50">
            {busy === 'impact' ? 'Requesting…' : 'Request impact analysis'}
          </button>
          <button type="button" onClick={() => request('documentation')} disabled={!!busy} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50">
            {busy === 'documentation' ? 'Requesting…' : 'Send a document request'}
          </button>
          <button type="button" onClick={() => request('question')} disabled={!!busy} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50">
            {busy === 'question' ? 'Asking…' : 'Ask a question'}
          </button>
        </div>
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}

      {requests.length > 0 && (
        <div className="space-y-2">
          {requests.map(r => (
            <AnalysisRow key={r.id} r={r} auditId={auditId} findingTitle={titleOf(r.finding_id)} documents={documents} onChanged={load} onEmail={onEmail} />
          ))}
        </div>
      )}
    </div>
  );
}
